import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", company: "", message: "" };

export default function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 5) {
      toast.error("Tell me a bit more about your project.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. I'll get back to you shortly.");
      setForm(EMPTY);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field = (name) => ({
    value: form[name],
    onChange: (e) => setForm({ ...form, [name]: e.target.value }),
    "data-testid": `form-contact-${name}`,
    className:
      "w-full bg-transparent border-b border-ink-border text-bone px-0 py-4 font-manrope text-base placeholder:text-bone/30 focus:outline-none focus:border-bone transition-colors",
  });

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2"
    >
      <Label text="Name" />
      <Label text="Email" />
      <input {...field("name")} placeholder="Your name" required maxLength={120} />
      <input {...field("email")} placeholder="hello@domain.com" type="email" required />

      <Label text="Brand / Company (optional)" className="md:col-span-2 mt-6" />
      <input
        {...field("company")}
        placeholder="e.g. Brand X, Studio Y…"
        className={field("company").className + " md:col-span-2"}
        maxLength={200}
      />

      <Label text="Tell me about the project" className="md:col-span-2 mt-6" />
      <textarea
        {...field("message")}
        placeholder="Idea, goal, brand, dates…"
        rows={4}
        required
        minLength={5}
        maxLength={5000}
        className={field("message").className + " md:col-span-2 resize-none"}
      />

      <div className="md:col-span-2 flex items-center justify-end pt-8">
        <button
          type="submit"
          disabled={loading}
          data-testid="form-contact-submit"
          className="group inline-flex items-center gap-3 bg-bone text-ink font-oswald uppercase tracking-tight text-lg px-6 py-3 hover:bg-bone/90 disabled:opacity-60 transition-colors"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
          {loading ? "Sending…" : "Send brief"}
        </button>
      </div>
    </form>
  );
}

function Label({ text, className = "" }) {
  return (
    <div className={`font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/50 mt-4 ${className}`}>
      {text}
    </div>
  );
}
